import React, { useEffect, useState, useContext } from "react";
import { Select } from "../../../components/select";
import {
  PERCENTAGES,
  TIME_FRAMES,
  MULTIPLES_PE_RATIO,
  MULTIPLES_PB_RATIO,
  PERCENTAGES_DIVIDEND_YIELD,
  PERCENTAGES_DIVIDEND_PAYOUT,
  MULTIPLES_DE_RATIO,
  PERCENTAGES_GROSS_MARGIN,
} from "./optionsData";
import {
  useGetAllMarketHook,
  useGetAllSectors,
} from "../../../hooks/useGetScreenerData.hook";
import CustomSelect from "./customSelect";
import { StateContext } from "../../../store/context";
import { TitleArea } from "../../../components/Typography/titleArea";

type FilterCategory = {
  id: string;
  label: string;
  options: any[];
  stateKey: string;
  mode?: "multiple" | undefined;
};

type FilterState = {
  marketId: number[] | null;
  sectorId: number[] | null;
  timeFrame: string | null;
  baseCurrency: string | null;
  priceToEarnings: number | null;
  pricesToBook: number | null;
  dividendYield: number | null;
  dividendPayout: number | null;
  debtEquity: number | null;
  epsGrowth: number | null;
  grossMargin: number | null;
  netMargin: null;
  operatingCashMargin: null;
};

type FormatConfig = {
  decimal: string;
  scale: string;
};

type FilterSectionType = {
  setFilters: any;
  onFormatChange?: (formatConfig: FormatConfig) => void;
};

export const FilterSection = (props: FilterSectionType) => {
  const { data, isLoading: marketLoading } = useGetAllMarketHook();
  const { data: sectors, isLoading: sectorLoading } = useGetAllSectors();
  const { authUser } = useContext(StateContext);
  const { role_name, sub_plan, role_id } = authUser;
  const isBasicCustomer =
    sub_plan === "Free Plan" && role_name === "Basic Customer";

  const [activeDecimalFormat, setActiveDecimalFormat] =
    useState<string>("decimal_00");
  const [activeScaleFormat, setActiveScaleFormat] = useState<string>("");

  const [filterState, setFilterState] = useState<FilterState>({
    marketId: null,
    sectorId: null,
    timeFrame: isBasicCustomer ? "ltm" : "last_quater",
    baseCurrency: null,
    priceToEarnings: null,
    pricesToBook: null,
    dividendYield: null,
    dividendPayout: null,
    debtEquity: null,
    epsGrowth: null,
    grossMargin: null,
    netMargin: null,
    operatingCashMargin: null,
  });

  const hasRestrictedAccess = role_id === 2 || role_id === 3;
  const allowedMarketIds = [1, 6];

  const EXCHANGE = [
    // { label: "All", value: "" },
    ...(data?.data
      ?.filter((market: any) => {
        if (market.status !== 1) return false;

        if (hasRestrictedAccess) {
          return allowedMarketIds.includes(market.id);
        }

        return true;
      })
      .map((market: any) => ({
        label: market.market_name,
        value: market.id,
      })) || []),
  ];

  const SECTORS = [
    // { label: "All", value: "" },
    ...(sectors?.data?.map((sector: any) => ({
      label: sector?.sector_name,
      value: sector?.id,
    })) || []),
  ];

  const FILTERED_TIME_FRAMES = isBasicCustomer
    ? TIME_FRAMES.filter((t) => t.value === "ltm")
    : TIME_FRAMES;

  const formatButtonGroups = [
    {
      type: "decimal",
      buttons: [
        { label: ".0", value: "decimal_0" },
        { label: ".00", value: "decimal_00" },
      ],
    },
    {
      type: "scale",
      buttons: [
        { label: "K", value: "thousands" },
        { label: "M", value: "millions" },
        { label: "B", value: "billions" },
      ],
    },
  ];

  const filterCategories: FilterCategory[] = [
    {
      id: "exchange",
      label: "Exchange",
      options: EXCHANGE,
      stateKey: "marketId",
      mode: "multiple",
    },
    {
      id: "sector",
      label: "Sector",
      options: SECTORS,
      stateKey: "sectorId",
      mode: "multiple",
    },
    {
      id: "time_frame",
      label: "Time Frame",
      options: FILTERED_TIME_FRAMES,
      stateKey: "timeFrame",
    },
    {
      id: "price_to_earnings",
      label: "Price to Earnings",
      options: MULTIPLES_PE_RATIO,
      stateKey: "priceToEarnings",
    },
    {
      id: "price_to_book",
      label: "Price to Book",
      options: MULTIPLES_PB_RATIO,
      stateKey: "pricesToBook",
    },
    {
      id: "dividend_yield",
      label: "Dividend Yield",
      options: PERCENTAGES_DIVIDEND_YIELD,
      stateKey: "dividendYield",
    },
    {
      id: "dividend_payout",
      label: "Dividend Payout",
      options: PERCENTAGES_DIVIDEND_PAYOUT,
      stateKey: "dividendPayout",
    },
    {
      id: "equity",
      label: "Debt to Equity",
      options: MULTIPLES_DE_RATIO,
      stateKey: "debtEquity",
    },
    {
      id: "growth",
      label: "EPS Growth",
      options: PERCENTAGES,
      stateKey: "epsGrowth",
    },
    {
      id: "margin",
      label: "Gross Margin",
      options: PERCENTAGES_GROSS_MARGIN,
      stateKey: "grossMargin",
    },
    {
      id: "net_margin",
      label: "Net Margin",
      options: PERCENTAGES,
      stateKey: "netMargin",
    },
    {
      id: "operating_cash_margin",
      label: "Operating Cash Margin",
      options: PERCENTAGES_GROSS_MARGIN,
      stateKey: "operatingCashMargin",
    },
  ];

  const handleSelectChange = (
    selectedOption: any,
    category: FilterCategory
  ) => {
    if (category.mode === "multiple") {
      setFilterState((prevState) => ({
        ...prevState,
        [category.stateKey]: selectedOption,
      }));
    } else {
      setFilterState((prevState) => ({
        ...prevState,
        [category.stateKey]: selectedOption || null,
      }));
    }
  };

  const handleFormatButtonClick = (format: string, groupType: string) => {
    if (groupType === "decimal") {
      const newDecimalFormat = activeDecimalFormat === format ? "" : format;
      setActiveDecimalFormat(newDecimalFormat);

      const formatConfig = {
        decimal: newDecimalFormat,
        scale: activeScaleFormat,
      };
      props.onFormatChange?.(formatConfig);
    } else if (groupType === "scale") {
      const newScaleFormat = activeScaleFormat === format ? "" : format;
      setActiveScaleFormat(newScaleFormat);

      const formatConfig = {
        decimal: activeDecimalFormat,
        scale: newScaleFormat,
      };
      props.onFormatChange?.(formatConfig);
    }
  };

  useEffect(() => {
    props.setFilters(filterState);
  }, [filterState]);

  return (
    <div className="vi-glass-card vi-scr-filter-panel space-y-4 p-5">
      {/* Format Buttons */}
      <div className="flex justify-between items-center gap-3">
        <TitleArea title="Filter List" />
        <div className="flex items-center gap-3">
          {formatButtonGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="flex items-center">
              {group.buttons.map((button, buttonIndex) => {
                const isActive =
                  group.type === "decimal"
                    ? activeDecimalFormat === button.value
                    : activeScaleFormat === button.value;
                return (
                  <button
                    key={button.value}
                    onClick={() =>
                      handleFormatButtonClick(button.value, group.type)
                    }
                    className={`
                px-3 py-1.5 text-sm font-medium border transition-colors duration-200
                ${buttonIndex === 0 ? "rounded-l-md" : ""}
                ${
                  buttonIndex === group.buttons.length - 1 ? "rounded-r-md" : ""
                }
                ${buttonIndex > 0 ? "border-l-0" : ""}
                ${
                  isActive
                    ? "bg-gray-300/60 text-gray-700 border-gray-300/60 z-10 relative"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }
              `}
                  >
                    {button.label}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-y-2">
        {filterCategories?.map((category) => (
          <div key={category.id} className="flex items-center gap-2">
            <label
              htmlFor={category.id}
              className="text-[14px] !w-[140px] font-medium text-gray-700  text-right !overflow-hidden !text-nowrap truncate font-dm"
            >
              {category.label}
            </label>
            {category.mode === "multiple" ? (
              <CustomSelect
                id={category.id}
                options={category.options}
                value={
                  filterState[category.stateKey as keyof FilterState] as any
                }
                onChange={(selectedOption) =>
                  handleSelectChange(selectedOption, category)
                }
                placeholder={`Select ${category.label}`}
                loading={marketLoading || sectorLoading}
                mode="multiple"
              />
            ) : (
              <Select
                id={category.id}
                loading={marketLoading || sectorLoading}
                options={category.options}
                placeholder={`Select ${category.label}`}
                className="!w-full !max-w-[140px] !font-dm"
                popupClassName="vi-scr-popup"
                value={
                  filterState[category.stateKey as keyof FilterState] as string
                }
                onChange={(selectedOption) =>
                  handleSelectChange(selectedOption, category)
                }
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
